import { useEffect, useState } from 'react';

const PANEL_OPEN_CLASS = 'atlas-gn-panel--open';
const POSITION_TIMEOUT_MS = 5000;

/**
 * Atlas's global-nav applies this class to `document.body` once it has
 * connected and computed its content-shifting margin (see the package's
 * bodyStyles — `[data-atlas-gn-app]`'s margin reads a CSS var that's only
 * set once this class is present). Neither the nav's own render nor its
 * lazy dev-mock chunk resolving guarantee this has happened yet — the
 * custom element's connect + internal layout logic is a separate async
 * step React has no visibility into. Observing this class directly is the
 * only way to know content can safely render without flashing unpositioned.
 *
 * `enabled` starts false on InternalRouting's first render (before feature
 * flags load) and flips true once ATLAS_GLOBAL_NAV resolves — so the check
 * for the class must re-run on every `enabled` change, not just once at
 * mount, or a stale "already positioned" bypasses the gate permanently.
 */
export const useAtlasNavPositioned = (enabled: boolean) => {
  const [isPositioned, setIsPositioned] = useState(false);

  useEffect(() => {
    if (!enabled) {
      // Reset so a later re-enable re-checks the class instead of reusing
      // stale readiness (e.g. from a prior timeout release).
      setIsPositioned(false);
      return;
    }
    if (isPositioned) return;

    if (document.body.classList.contains(PANEL_OPEN_CLASS)) {
      setIsPositioned(true);
      return;
    }

    const observer = new MutationObserver(() => {
      if (document.body.classList.contains(PANEL_OPEN_CLASS)) {
        setIsPositioned(true);
        observer.disconnect();
      }
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // If the global-nav web component never applies the class (failed load,
    // unexpected markup), release the gate anyway so content isn't blocked
    // forever behind a silent spinner.
    const timeoutId = window.setTimeout(() => {
      observer.disconnect();
      console.warn(
        `useAtlasNavPositioned: "${PANEL_OPEN_CLASS}" was never applied to document.body after ${POSITION_TIMEOUT_MS}ms; releasing the positioning gate.`,
      );
      setIsPositioned(true);
    }, POSITION_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [enabled, isPositioned]);

  return !enabled || isPositioned;
};
